// Global Types
import type {
  $RedirectParams,
  $RedirectResponse,
} from '@vroskus/library-navigation';
import type {
  $FullRegion,
  $Model,
} from 'common.data';

// Global Enums
import {
  RegattaStatus,
} from 'common.data';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  getRegionByRegionSeasonId,
  getShortItemUrl,
} from 'common.helpers/dist/browser';
import {
  getMenu,
} from '../../../routes/menu';
import config from '../../../config';

import * as mainActionTypes from './types';

// Types
import type {
  $Services,
} from '../../../services';
import type {
  $Action,
  $ActionResult,
  $Dispatch,
  $State,
} from '../..';

import type {
  $Menu,
} from '../../../routes/menu';

// redirect action
type $RedirectActionParams = $RedirectParams;
type $RedirectActionResponse = $RedirectResponse;
export type $RedirectAction = (
  arg0: $RedirectActionParams,
) => $ActionResult<$RedirectActionResponse>;

export const redirect: $Action<
$RedirectActionResponse,
$RedirectActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  {
    Navigation,
  }: $Services,
) => dispatch({
  payload: async () => Navigation.redirect(params),
  type: mainActionTypes.REDIRECT,
});

// openActiveRegattaPage action
type $OpenActiveRegattaPageActionParams = void;
type $OpenActiveRegattaPageActionResponse = void;
export type $OpenActiveRegattaPageAction = () => $ActionResult<
$OpenActiveRegattaPageActionResponse
>;

export const openActiveRegattaPage: $Action<
$OpenActiveRegattaPageActionResponse,
$OpenActiveRegattaPageActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
) => dispatch({
  payload: async () => {
    const state = getState();
    const {
      main,
    } = state;
    const {
      activeRegattaId,
      regattas,
      regions,
    } = main;

    const regatta = _.find(
      regattas,
      {
        id: activeRegattaId,
      },
    ) as $Model<'Regatta'> | undefined;

    if (regatta) {
      const regattaUrl: string = getShortItemUrl({
        basePath: '/regatta',
        item: regatta,
        mainDomain: config.DOMAIN,
        regions,
      });

      window.open(
        regattaUrl,
        '_blank',
      );
    }
  },
  type: mainActionTypes.OPEN_ACTIVE_REGATTA_PAGE,
});

// toggleRegionSelector action
type $ToggleRegionSelectorActionParams = boolean;
type $ToggleRegionSelectorActionResponse = boolean;
export type $ToggleRegionSelectorAction = (
  arg0: $ToggleRegionSelectorActionParams,
) => $ActionResult<$ToggleRegionSelectorActionResponse>;

export const toggleRegionSelector: $Action<
$ToggleRegionSelectorActionResponse,
$ToggleRegionSelectorActionParams
> = (params) => (
  dispatch: $Dispatch,
) => dispatch({
  payload: async () => params,
  type: mainActionTypes.TOGGLE_REGION_SELECTOR,
});

// getRegions action
type $GetRegionsActionPrams = void;
type $GetRegionsActionResponse = Array<$FullRegion>;
export type $GetRegionsAction = () => $ActionResult<$GetRegionsActionResponse>;

export const getRegions: $Action<
$GetRegionsActionResponse,
$GetRegionsActionPrams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => services.Api.getRegions(),
  type: mainActionTypes.GET_REGIONS,
});

// setActiveRegionId action
type $SetActiveRegionIdActionParams = {
  RegionId: string;
};
type $SetActiveRegionIdActionResponse = string;
export type $SetActiveRegionIdAction = (
  arg0: $SetActiveRegionIdActionParams,
) => $ActionResult<$SetActiveRegionIdActionResponse>;

export const setActiveRegionId: $Action<
$SetActiveRegionIdActionResponse,
$SetActiveRegionIdActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    await services.LocalStorage.setItem(
      'RegionId',
      params.RegionId,
    );

    return params.RegionId;
  },
  type: mainActionTypes.SET_ACTIVE_REGION_ID,
});

// setLanguage action
type $SetLanguageActionParams = {
  language: string;
};
type $SetLanguageActionResponse = {
  language: string;
};
export type $SetLanguageAction = (
  arg0: $SetLanguageActionParams,
) => $ActionResult<$SetLanguageActionResponse>;

export const setLanguage: $Action<
$SetLanguageActionResponse,
$SetLanguageActionParams
> = ({
  language,
}) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    services.Cookies.setItem(
      'language',
      language,
    );

    i18next.changeLanguage(language);

    return {
      language,
    };
  },
  type: mainActionTypes.SET_LANGUAGE,
});

// setupLanguage action
type $SetupLanguageActionParams = void;
type $SetupLanguageActionResponse = void;
export type $SetupLanguageAction = (
  arg0: $SetupLanguageActionParams,
) => $ActionResult<$SetupLanguageActionResponse>;

export const setupLanguage: $Action<
$SetupLanguageActionResponse,
$SetupLanguageActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    let language = services.Cookies.getItem('language');

    if (language) {
      i18next.changeLanguage(language);
    } else {
      language = i18next.language;
    }

    await setLanguage({
      language,
    })(
      dispatch,
      getState,
      services,
    );
  },
  type: mainActionTypes.SETUP_LANGUAGE,
});

// getRegattas action
type $GetRegattasActionParams = {
  RegionSeasonId: string;
};
type $GetRegattasActionResponse = Array<$Model<'Regatta'>>;
export type $GetRegattasAction = (
  arg0: $GetRegattasActionParams,
) => $ActionResult<$GetRegattasActionResponse>;

export const getRegattas: $Action<
$GetRegattasActionResponse,
$GetRegattasActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => services.Api.getManyRegatta(params),
  type: mainActionTypes.GET_REGATTAS,
});

// setupMenu action
type $SetupMenuActionParams = {
  RegattaId: null | string;
};
type $SetupMenuActionResponse = {
  menu: $Menu;
};
export type $SetupMenuAction = (
  arg0: $SetupMenuActionParams,
) => $ActionResult<$SetupMenuActionResponse>;

export const setupMenu: $Action<
$SetupMenuActionResponse,
$SetupMenuActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
) => dispatch({
  payload: async () => {
    const state = getState();
    const {
      auth,
    } = state;

    const menu = getMenu({
      isSuperUser: auth.user !== null && auth.user.Super === true,
      RegattaId: params.RegattaId,
    });

    return {
      menu,
    };
  },
  type: mainActionTypes.SETUP_MENU,
});

// setupUser action
type $SetupUserActionParams = $Model<'Admin'>;
type $SetupUserActionResponse = $Model<'Admin'>;
export type $SetupUserAction = (
  arg0: $SetupUserActionParams,
) => $ActionResult<$SetupUserActionResponse>;

export const setupUser: $Action<
$SetupUserActionResponse,
$SetupUserActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    ErrorLog.setUser({
      email: params.Email,
      id: params.id,
      username: params.Name,
    });

    services.Mixpanel.setUser({
      $email: params.Email,
      $name: params.Name,
      id: params.id,
    });

    services.Crisp.setUser(params);

    return params;
  },
  type: mainActionTypes.SETUP_USER,
});

// setActiveRegattaId action
type $SetActiveRegattaIdActionParams = {
  RegattaId: null | string;
};
type $SetActiveRegattaIdActionResponse = {
  RegattaId: null | string;
};
export type $SetActiveRegattaIdAction = (
  arg0: $SetActiveRegattaIdActionParams,
) => $ActionResult<$SetActiveRegattaIdActionResponse>;

export const setActiveRegattaId: $Action<
$SetActiveRegattaIdActionResponse,
$SetActiveRegattaIdActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    const state = getState();
    const {
      regattas,
    } = state.main;

    const regatta = _.find(
      regattas,
      {
        id: params.RegattaId,
      },
    ) as $Model<'Regatta'> | undefined;

    let RegattaId: null | string = null;

    if (regatta) {
      RegattaId = regatta.id;

      await services.LocalStorage.setItem(
        'RegattaId',
        RegattaId,
      );

      services.Crisp.setAttr({
        key: 'Regatta',
        value: regatta.Name,
      });
    } else {
      await services.LocalStorage.removeItem('RegattaId');
    }

    await setupMenu({
      RegattaId,
    })(
      dispatch,
      getState,
      services,
    );

    return params;
  },
  type: mainActionTypes.SET_ACTIVE_REGATTA_ID,
});

// setFirstActiveRegattaId
type $SetLastActiveRegattaIdActionParams = void;
type $SetLastActiveRegattaIdActionResponse = void;
export type $SetLastActiveRegattaIdAction = () => $ActionResult<
$SetLastActiveRegattaIdActionResponse
>;

export const setFirstActiveRegattaId: $Action<
$SetLastActiveRegattaIdActionResponse,
$SetLastActiveRegattaIdActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    const state = getState();
    const {
      activeRegattaId,
      regattas,
    } = state.main;

    if (regattas.length > 0) {
      const regatta = _.find(
        regattas,
        {
          id: activeRegattaId,
        },
      ) as $Model<'Regatta'> | undefined;

      // If saved regatta id is not on the regattas list, set first proper regatta on list as active
      if (!regatta) {
        let RegattaId: null | string = null;

        regattas.forEach((dataItem) => {
          if ([
            RegattaStatus.ended,
            RegattaStatus.ongoing,
          ].includes(dataItem.Status) || RegattaId === null) {
            RegattaId = dataItem.id;
          }
        });

        await setActiveRegattaId({
          RegattaId,
        })(
          dispatch,
          getState,
          services,
        );
      }
    }
  },
  type: mainActionTypes.SET_LAST_ACTIVE_REGATTA_ID,
});

// setActiveRegionSeasonId action
type $SetActiveRegionSeasonIdActionParams = {
  RegionSeasonId: string;
  resetRegatta?: boolean;
};
type $SetActiveRegionSeasonIdActionResponse = string;
export type $SetActiveRegionSeasonIdAction = (
  arg0: $SetActiveRegionSeasonIdActionParams,
) => $ActionResult<$SetActiveRegionSeasonIdActionResponse>;

export const setActiveRegionSeasonId: $Action<
$SetActiveRegionSeasonIdActionResponse,
$SetActiveRegionSeasonIdActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    await services.LocalStorage.setItem(
      'RegionSeasonId',
      params.RegionSeasonId,
    );

    await getRegattas({
      RegionSeasonId: params.RegionSeasonId,
    })(
      dispatch,
      getState,
      services,
    );

    if (params.resetRegatta === true) {
      await setActiveRegattaId({
        RegattaId: null,
      })(
        dispatch,
        getState,
        services,
      );
    }

    return params.RegionSeasonId;
  },
  type: mainActionTypes.SET_ACTIVE_REGION_SEASON_ID,
});

// setupMainData action
type $SetupMainDataActionParams = {
  RegattaId?: string;
  RegionSeasonId?: string;
};
type $SetupMainDataActionResponse = void;
export type $SetupMainDataAction = (
  arg0: $SetupMainDataActionParams,
) => $ActionResult<$SetupMainDataActionResponse>;

export const setupMainData: $Action<
$SetupMainDataActionResponse,
$SetupMainDataActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  // eslint-disable-next-line complexity
  payload: async () => {
    const state = getState();
    const {
      regions,
    } = state.main;

    const savedRegionId: null | string = await services.LocalStorage.getItem('RegionId');

    const region = params.RegionSeasonId
      ? getRegionByRegionSeasonId({
        regions,
        RegionSeasonId: params.RegionSeasonId,
      })
      : null;

    const RegionId: null | string = region !== null ? region.id : savedRegionId;

    const savedRegionSeasonId: null | string = await services.LocalStorage.getItem('RegionSeasonId');

    const RegionSeasonId: null | string = params.RegionSeasonId || savedRegionSeasonId;

    if (RegionId !== null && RegionSeasonId !== null) {
      await setActiveRegionId({
        RegionId,
      })(
        dispatch,
        getState,
        services,
      );

      await setActiveRegionSeasonId({
        RegionSeasonId,
      })(
        dispatch,
        getState,
        services,
      );

      const savedRegattaId: null | string = await services.LocalStorage.getItem('RegattaId');

      const RegattaId: string | undefined = params.RegattaId || savedRegattaId;

      if (RegattaId !== null) {
        await setActiveRegattaId({
          RegattaId,
        })(
          dispatch,
          getState,
          services,
        );
      } else {
        await setFirstActiveRegattaId()(
          dispatch,
          getState,
          services,
        );
      }
    } else {
      await toggleRegionSelector(true)(
        dispatch,
        getState,
        services,
      );
    }
  },
  type: mainActionTypes.SETUP_MAIN_DATA,
});

// setStart action
type $SetStartActionParams = moment.Moment | null;
type $SetStartActionResponse = moment.Moment | null;
export type $SetStartAction = (
  arg0: $SetStartActionParams,
) => $ActionResult<$SetStartActionResponse>;

export const setStart: $Action<
$SetStartActionResponse,
$SetStartActionParams
> = (params) => (
  dispatch: $Dispatch,
) => dispatch({
  payload: async () => params,
  type: mainActionTypes.SET_START,
});
